import { useEffect, useState } from 'react';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useClientOrg, useMe } from '@/api/hooks';
import type { ClientOrg, ClientOrgDeletion } from '@/api/schemasMembers';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ClientDangerZone } from '@/features/clients/ClientDangerZone';
import { ClientDetailsCard } from '@/features/clients/ClientDetailsCard';
import { ClientPeopleCard } from '@/features/clients/ClientPeopleCard';
import { ClientProviders } from '@/features/clients/ClientProviders';
import { ClientStatusBadge } from '@/features/clients/ClientStatusBadge';
import { ClientStatusCard } from '@/features/clients/ClientStatusCard';
import { DeletionProgress } from '@/features/clients/DeletionProgress';
import { CLIENT_SECTIONS, clientActions, clientSectionAnchor, type ClientSection } from '@/features/clients/clientLifecycleModel';
import { useFeature } from '@/lib/permissions';
import { useOrgActions } from '@/shell/useOrgActions';

export interface ClientDetailSearch {
  section?: ClientSection;
}

export const clientDetailSearch = (search: Record<string, unknown>): ClientDetailSearch => ({
  section: typeof search.section === 'string' && (CLIENT_SECTIONS as readonly string[]).includes(search.section) ? (search.section as ClientSection) : undefined,
});

/**
 * One client (platform operators, SPEC decision 26 §17.6): a page, not a modal, as it has sub-resources edited apart —
 * details, people (invitations), status (suspend, reactivate) and the danger zone (delete, its steps and manual removals).
 * `?section=` lands on a card, so the Clients row actions open the page where they act.
 */
export function ClientDetailPage() {
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const me = useMe();
  const served = useFeature('clients');
  const operator = me.data?.user?.platformRole === 'operator';
  if (me.isPending) return <DetailSkeleton />;
  if (!operator || served === false) {
    return <Card className="enter"><EmptyState testId="clients-locked" icon="lock" title="Only platform operators manage clients" description="Only Hangar's platform operators can open a client." /></Card>;
  }
  return <ClientDetail orgId={orgId} />;
}

function ClientDetail({ orgId }: { orgId: string }) {
  const search = useSearch({ strict: false }) as ClientDetailSearch;
  const client = useClientOrg(orgId);
  const [deletion, setDeletion] = useState<ClientOrgDeletion | null>(null);
  useScrollToClientSection(client.org?.id, search.section);
  if (client.isPending) return <DetailSkeleton />;
  if (client.isError) return <ErrorState error={client.error} onRetry={() => void client.refetch()} title="Couldn't load the client" />;
  if (!client.org) return deletion ? <DeletedClient deletion={deletion} /> : <ClientNotFound />;
  const org = client.org;
  return (
    <div data-testid="client-detail" className="flex min-w-0 flex-col gap-6">
      <DetailHeader org={org} />
      <ClientDetailsCard key={`${org.name}|${org.emailDomains.join(',')}`} org={org} id={clientSectionAnchor('details')} index={1} />
      <ClientPeopleCard org={org} id={clientSectionAnchor('people')} index={2} />
      <ClientStatusCard org={org} id={clientSectionAnchor('status')} index={3} />
      <ClientDangerZone org={org} id={clientSectionAnchor('danger')} index={4} onDeletion={setDeletion} />
    </div>
  );
}

function BackToClients() {
  return (
    <Link to="/clients" data-testid="client-back" className="flex min-h-11 w-fit items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground sm:min-h-0">
      <Icon name="arrow_back" size={16} />
      Clients
    </Link>
  );
}

function DetailHeader({ org }: { org: ClientOrg }) {
  const { startPreview } = useOrgActions();
  const canPreview = clientActions(org.status).includes('preview');
  return (
    <header className="enter flex flex-col gap-2 border-b pb-5">
      <BackToClients />
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h1 data-testid="client-title" className="min-w-0 break-words text-2xl font-semibold tracking-tight text-foreground">{org.name}</h1>
          <Tag>{org.slug}</Tag>
          <ClientStatusBadge status={org.status} testId="client-header-status" />
        </div>
        {canPreview ? (
          <Button variant="outline" data-testid="client-detail-preview" className="max-sm:h-11" onClick={() => startPreview({ id: org.id, name: org.name, ...(org.status === 'suspended' ? { suspended: true } : {}) })}>
            <Icon name="visibility" size={18} />
            Preview as client
          </Button>
        ) : null}
      </div>
      <ClientProviders o={org} />
    </header>
  );
}

function DeletedClient({ deletion }: { deletion: ClientOrgDeletion }) {
  return (
    <div data-testid="client-deleted" className="flex min-w-0 flex-col gap-6">
      <header className="enter flex flex-col gap-2 border-b pb-5">
        <BackToClients />
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Client deleted</h1>
        <p className="max-w-[72ch] text-sm text-muted-foreground">It is gone from Hangar. Finish the provider items below by hand: this list is not shown again once you leave the page.</p>
      </header>
      <Card className="enter"><CardContent className="pt-6"><DeletionProgress steps={deletion.steps} /></CardContent></Card>
    </div>
  );
}

function ClientNotFound() {
  return (
    <EmptyState
      testId="client-not-found"
      icon="domain_disabled"
      title="Client not found"
      description="There is no client with this id: it may have been deleted."
      action={<Button asChild variant="outline"><Link to="/clients">Back to Clients</Link></Button>}
    />
  );
}

function DetailSkeleton() {
  return (
    <div data-testid="client-detail-loading" className="flex flex-col gap-6" aria-busy="true" aria-label="Loading the client">
      <div className="flex flex-col gap-2 border-b pb-5">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
      </div>
      {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}
    </div>
  );
}

/** `?section=`: land on that card once the client is in (smooth, or a jump under reduced motion). */
function useScrollToClientSection(orgId: string | undefined, section: ClientSection | undefined) {
  useEffect(() => {
    if (!orgId || !section) return;
    const t = setTimeout(() => {
      const el = document.getElementById(clientSectionAnchor(section));
      if (!el) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      if (section === 'people') document.getElementById('client-invite-email')?.focus({ preventScroll: true });
    }, 60);
    return () => clearTimeout(t);
  }, [orgId, section]);
}
